import type { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Check12 as IconCheck } from '@elfsight/icons';
// import IconCheck from './icon-check.svg?react';
import { getColorWithAlpha, getInvertedColor } from '@elfsight/widget-utils';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { useFiltersTheme } from '../../../filters-theme-provider';

export type CheckboxProps = HTMLAttributes<HTMLButtonElement> & {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

export function Checkbox({
  checked,
  defaultChecked,
  onCheckedChange,
  ...forwardingProps
}: CheckboxProps) {
  const { accentColor, textColor } = useFiltersTheme();

  return (
    <Root
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange}
      $textColor={textColor}
      $accentColor={accentColor}
      {...forwardingProps}
    >
      <Indicator>
        <IconCheck />
      </Indicator>
    </Root>
  );
}

const Indicator = styled(RadixCheckbox.Indicator)`
  display: inline-flex;
  opacity: 1;
  transition: opacity 0.2s ease-out;

  @starting-style {
    & {
      opacity: 0;
    }
  }
`;

const Root = styled(RadixCheckbox.Root)<{
  $textColor: string;
  $accentColor: string;
}>`
  border-width: 1px;
  border-style: solid;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  ${({ $textColor, $accentColor }) => css`
    border-color: ${getColorWithAlpha($textColor, 0.3)};
    background-color: transparent;
    & svg {
      fill: ${getInvertedColor($accentColor)};
    }

    &[data-state='checked'] {
      background-color: ${$accentColor};
      border-color: transparent;
    }

    &[data-state='unchecked']:hover {
      border-color: ${getColorWithAlpha($textColor, 0.7)};
      background-color: ${getColorWithAlpha($textColor, 0.02)};
    }
  `}

  width: 20px;
  height: 20px;
  ${Indicator} {
    width: 12px;
    height: 12px;
  }
`;
